import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { OPS_ENDPOINTS } from "@src/utility/constants/endpoints";

const DEFAULT_LIMIT = 10;

const useDebitNotes = (brandId) => {
    const { axiosInstance, loading } = useAxios({ base: "ops", initialLoader: true });
    const { axiosInstance: invoicesAxiosInstance } = useAxios({ base: "ops" });
    const [notes, setNotes] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT, totalPages: 1 });
    const [filters, setFilters] = useState({ status: "" });
    // The note the revert modal is open for, if any.
    const [revertTarget, setRevertTarget] = useState(null);

    const fetchNotes = async (page = 1, limit = DEFAULT_LIMIT, currentFilters = filters) => {
        const params = { page, limit };
        if (currentFilters.status) params.status = currentFilters.status;

        const res = await axiosInstance.get(OPS_ENDPOINTS.BRAND_DEBIT_NOTES(brandId), { params });
        const result = res.data || res;
        const items = result.items || result.data || result.results || [];
        setNotes(items);
        setPagination({
            total     : result.total ?? items.length,
            page      : result.page ?? page,
            limit     : result.limit ?? limit,
            totalPages: result.totalPages ?? 1
        });
    };

    useEffect(() => {
        fetchNotes(1, DEFAULT_LIMIT, filters);
    }, [brandId, filters]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (limit, page) => fetchNotes(page, limit);
    const handlePerRowsChange = (limit) => fetchNotes(1, limit);
    const handleFilterChange = (nextFilters) => setFilters((prev) => ({ ...prev, ...nextFilters }));

    const updateStatus = async (noteId, status, reason) => {
        await axiosInstance.patch(OPS_ENDPOINTS.DEBIT_NOTE_STATUS(brandId, noteId), reason ? { status, reason } : { status });
        await fetchNotes(pagination.page, pagination.limit);
    };

    const revertNote = async (noteId, reason) => {
        await axiosInstance.patch(OPS_ENDPOINTS.DEBIT_NOTE_REVERT(brandId, noteId), reason ? { reason } : undefined);
        await fetchNotes(pagination.page, pagination.limit);
    };

    const markAsReceived = (noteId) => updateStatus(noteId, "fund_received");
    const issueToBrand = (noteId) => updateStatus(noteId, "issued");

    const fetchInvoices = async (noteId) => {
        try {
            const res = await invoicesAxiosInstance.get(OPS_ENDPOINTS.DEBIT_NOTE_INVOICES(brandId, noteId), { skipSessionRedirect: true });
            const result = res.data || res;
            return result.items || result.data || result.results || [];
        } catch {
            return [];
        }
    };

    return {
        notes,
        pagination,
        loading,
        filters,
        handleFilterChange,
        revertTarget,
        openRevert : setRevertTarget,
        closeRevert: () => setRevertTarget(null),
        revertNote,
        markAsReceived,
        issueToBrand,
        fetchInvoices,
        handlePageChange,
        handlePerRowsChange
    };
};

export default useDebitNotes;
